import { ArrowRight, CheckCircle2, Download, Send } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import type { IntakeLoad } from "@/features/intake/api/types"
import { BordereauBreakdown } from "@/features/intake/components/bordereau-breakdown"
import { downloadBordereauPdf } from "@/features/intake/lib/bordereau-pdf"
import { extractApiErrorMessage } from "@/lib/api/client"
import { paths } from "@/routes/paths"

interface BordereauCardProps {
  load: IntakeLoad
}

export function BordereauCard({ load }: BordereauCardProps) {
  const { t } = useTranslation("intake")
  const [downloading, setDownloading] = useState(false)
  const number = load.bordereauNumber

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      await downloadBordereauPdf(load, t)
    } catch (err) {
      toast.error(extractApiErrorMessage(err))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
          <CheckCircle2 className="size-3.5" />
          {t("completed.bordereauIssued")}
        </div>
        <CardTitle className="text-2xl tabular-nums">
          {number ? t("completed.bordereauNumber", { number }) : "—"}
        </CardTitle>
        {number ? (
          <div className="text-muted-foreground text-xs">
            {t("completed.bordereauNumberCaption")}
          </div>
        ) : null}
        <CardDescription>{t("completed.subtitle")}</CardDescription>
        <CardAction>
          <Badge
            variant="outline"
            className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30 text-[10px] uppercase tracking-wide"
          >
            {t("completed.bonPourPaiement")}
          </Badge>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <BordereauBreakdown load={load} />

          <div className="flex flex-col gap-2">
            <Button variant="outline" disabled title={t("completed.comingSoon")}>
              <Send className="size-4" />
              {t("completed.sendToCooperative")}
            </Button>
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={downloading}
            >
              <Download className="size-4" />
              {t("completed.downloadPdf")}
            </Button>
            <Button asChild>
              <Link to={paths.millFloor}>
                <ArrowRight className="size-4" />
                {t("completed.seeApprovedStock")}
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
